import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Grid2,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";
import { AddLocation } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { TimePickers } from "../../components/Pickers/time-pickers.component";
import MultiColorProgressBar from "../../components/Reports/MultiColorProgressBar/MultiColorProgressBar";
import { checkEndBeforeStart, checkStartInPast } from "../../utils/utilities";
import { ChosenTimesWarnings } from "./components/chosen-times-warnings.component";
import { BookingType } from "../Booking-Form/typings/booking-inputs";
import { useRemoteBookingOnTheFly } from "../../hooks/Remote/Checkin/OnTheFly/useRemoteBookingOnTheFly";
import {
  AvailabilityTimeOnTheFlyResponse,
  useRemoteFetchAvailabilityTimeOnTheFly
} from "../../hooks/Remote/Checkin/OnTheFly/useRemoteAvailabilityTimeOnTheFly";
import { useRemoteFetchCheckinInfo } from "../../hooks/Remote/Checkin/OnTheFly/useRemoteGetCheckinInfo";
import { useRemoteGetLocationGeoCodeByUid } from "../../hooks/Remote/Checkin/OnTheFly/useRemoteGetLocationGeoCodeByUid";
import { WorkplaceBooking } from "./typings";
import { getLaterTime, handleCheckinResponse } from "./functions/checkin";
import { CheckinBookingCard } from "./components/CheckinBookingCard.component";
import { CheckinHeader } from "./components/CheckinHeader.component";

export const arePointsNear = (
  checkPoint: { lng: number; lat: number },
  centerPoint: { lat: number; lng: number },
  km: number
) => {
  const ky = 40000 / 360;
  const kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
  const dx = Math.abs(centerPoint.lng - checkPoint.lng) * kx;
  const dy = Math.abs(centerPoint.lat - checkPoint.lat) * ky;
  return Math.hypot(dx, dy) < km;
};

export const Checkin = () => {
  const { search } = useLocation();
  const theme = useTheme();
  const {
    sub,
    company: {
      meta: { checkinSettings }
    }
  } = useSelector((state: RootState) => state.login.userInformation);
  const { standardEndTime } = useSelector((state: RootState) => state.login.settings);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [booking, setBooking] = useState<WorkplaceBooking[]>();
  const [open, setOpen] = useState(false);
  const [availabilityHours, setAvailabilityHours] = useState<
    AvailabilityTimeOnTheFlyResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [locationCoordinates, setLocationCoordinates] = useState<{ lat: number; lng: number }>(
    {} as { lat: number; lng: number }
  );
  const [position, setPosition] = useState({
    center: { lat: 43.642558, lng: -79.387046 }, // CN Tower default
    content: "Getting position...",
    insideFence: false,
    fetchingDone: false,
    lastFetched: 0
  });

  const [chosenTimes, setChosenTimes] = useState({
    startTime: DateTime.now().toFormat("HH:mm"),
    endTime: getLaterTime(standardEndTime, true)
  });

  const setError = () =>
    setPosition(prevState => ({
      ...prevState,
      center: { lat: 0, lng: 0 },
      content: `Blocked`,
      lastFetched: 0,
      fetchingDone: true,
      insideFence: false
    }));

  const {
    mutate: checkinOnTheFly,
    error: checkinOnTheFlyError,
    status: checkinOnTheFlyStatus,
    data: checkinOnTheFlyRes
  } = useRemoteBookingOnTheFly();

  const {
    data: checkinInfoData,
    status: checkinInfoStatus,
    isFetched: checkinInfoFetched,
    refetch: refetchCheckinInfo
  } = useRemoteFetchCheckinInfo(search.substring(1));

  const {
    data: availabilityTimeOnTheFlyData,
    isFetched: availabilityTimeOnTheFlyFetched,
    error: errAvailabilityTimeOnTheFly
  } = useRemoteFetchAvailabilityTimeOnTheFly({ uid: search.substring(1) });

  const { data: locationGeoCodeByUidData } = useRemoteGetLocationGeoCodeByUid({
    uid: search.substring(1)
  });

  const checkinAvailabilityColor = position.insideFence
    ? theme.palette.success.main
    : theme.palette.error.main;

  const hideDialog = (o: boolean) => {
    setOpen(o); // make it false
    setLoading(o); // make it false
  };

  const handleGetGeoLocation = () => {
    navigator.geolocation.getCurrentPosition(
      function (positionGEO) {
        setPosition(prevState => ({
          ...prevState,
          center: {
            lat: positionGEO.coords.latitude,
            lng: positionGEO.coords.longitude
          },
          content: `Location found.`,
          lastFetched: positionGEO.timestamp,
          fetchingDone: true,
          insideFence: arePointsNear(
            {
              lat: positionGEO.coords.latitude,
              lng: positionGEO.coords.longitude
            },
            { lat: locationCoordinates.lat, lng: locationCoordinates.lng },
            1
          )
        }));
      },
      () => {
        alert("Please enable geolocation");
        setError();
      }
    );
  };

  const handleConfirmBookAndCheckinOntheFly = () => {
    const geoRequired = checkinSettings.find(
      setting => setting.bookingType === availabilityHours?.bookingType
    )?.geoLocationRequired;

    setLoading(true);
    // spontaneous booking
    checkinOnTheFly({
      users: [
        {
          userId: sub,
          isExternal: false,
          bookingObjectOnTheFly: search.substring(1)
        }
      ],
      startDate: DateTime.now().toFormat("yyyy-MM-dd"),
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
      startTime: chosenTimes.startTime,
      endTime: chosenTimes.endTime,
      bookingType: availabilityHours?.bookingType || BookingType.WORKPLACE,
      latitude: geoRequired ? position.center.lat : 0,
      longitude: geoRequired ? position.center.lng : 0,
      locationInventoryId: 0
    });
  };

  useEffect(() => {
    const checkBookingType =
      availabilityHours?.bookingType || (booking?.length && booking[0].bookingType);
    const checkinSetting = checkinSettings.find(
      setting => checkBookingType === setting.bookingType
    );

    handleCheckinResponse(
      {
        checkinRes: checkinOnTheFlyRes,
        checkinError: checkinOnTheFlyError,
        errAvailabilityTimeOnTheFly,
        isCheckinPerBookingType: Boolean(checkinSetting),
        geoLocationRequired: Boolean(checkinSetting?.geoLocationRequired),
        checkinOnTheFly: true,
        isSameTimeNow: chosenTimes.startTime === DateTime.now().toFormat("HH:mm")
      },
      { t, enqueueSnackbar, refetchAllSchedule: refetchCheckinInfo, setShowDialog: hideDialog }
    );
    setLoading(false);
  }, [
    checkinOnTheFlyError,
    errAvailabilityTimeOnTheFly,
    checkinOnTheFlyStatus,
    checkinOnTheFlyRes
  ]);

  useEffect(() => {
    if (checkinInfoFetched && checkinInfoStatus === "success" && checkinInfoData) {
      setBooking(checkinInfoData);
    }
  }, [search, checkinInfoFetched, checkinInfoStatus, checkinOnTheFlyStatus]);

  useEffect(() => {
    // check bookingType and its geoLocationRequired in the company settings
    const initialBookingType =
      (booking?.length && booking[0].bookingType) || availabilityTimeOnTheFlyData?.bookingType;

    const geoLocationRequired =
      checkinSettings.find(setting => initialBookingType === setting.bookingType)
        ?.geoLocationRequired ?? false;

    // checking api data firstly before company setting
    if (!locationGeoCodeByUidData?.length) return;

    if (!geoLocationRequired) {
      return setPosition(prevState => ({
        ...prevState,
        content: "Not required",
        fetchingDone: true,
        insideFence: true
      }));
    }

    if ("geolocation" in navigator && locationGeoCodeByUidData.length) {
      const lati = locationGeoCodeByUidData[0].latitude;
      const lngi = locationGeoCodeByUidData[0].longitude;

      setLocationCoordinates({ lat: lati, lng: lngi });

      navigator.geolocation.getCurrentPosition(
        function (positionGEO) {
          setPosition(prevState => ({
            ...prevState,
            center: {
              lat: positionGEO.coords.latitude,
              lng: positionGEO.coords.longitude
            },
            content: `Location found.`,
            lastFetched: positionGEO.timestamp,
            fetchingDone: true,
            insideFence: arePointsNear(
              { lat: positionGEO.coords.latitude, lng: positionGEO.coords.longitude },
              { lat: lati, lng: lngi },
              30
            )
          }));
        },
        // handle error
        () => setError()
      );
    } else {
      alert(t("Geolocation is not supported by this browser"));
      setPosition(prevState => ({ ...prevState, fetchingDone: true }));
    }
  }, [search, locationGeoCodeByUidData, booking, position.fetchingDone]);

  const filterCurrentAvailabilityHours = (availabilityHours: AvailabilityTimeOnTheFlyResponse) => {
    //We only want timeFrames which are ongoing or upcoming
    const currentAvailabilityHours = availabilityHours.timeframes.filter(
      timeFrame => timeFrame.endTime > DateTime.now().toFormat("HH:mm")
    );

    if (currentAvailabilityHours.length) {
      //set startTime of currently ongoing timeframe to now
      currentAvailabilityHours[0].startTime = DateTime.now().toFormat("HH:mm");
    }

    return {
      bookingType: availabilityHours.bookingType,
      timeframes: currentAvailabilityHours
    };
  };

  const calcRelativeDurationForRemainingDay = (startTime: string, endTime: string) => {
    const now = DateTime.now();
    const remainingMinutesToday = now.endOf("day").diff(now, "minutes").as("minutes");

    return (
      DateTime.fromISO(endTime).diff(DateTime.fromISO(startTime), "minutes").as("minutes") /
      remainingMinutesToday
    );
  };

  return (
    <>
      <Grid2 container justifyContent={"space-between"} alignItems={"flex-start"}>
        {locationGeoCodeByUidData && locationGeoCodeByUidData.length > 0 && (
          <>
            <CheckinHeader
              booking={booking}
              checkinAvailabilityColor={checkinAvailabilityColor}
              position={position}
              handleGetGeoLocation={handleGetGeoLocation}
            />

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {!position.fetchingDone && (
                <Grid2
                  container
                  direction={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <CircularProgress />
                  <Typography variant={"caption"}>{t("_fetchingLocation")}</Typography>
                </Grid2>
              )}
              {position.fetchingDone &&
                position.insideFence &&
                position.content !== "Getting position..." && (
                  <>
                    <IconButton
                      data-testid={"book-now-btn"}
                      sx={{ width: "fit-content", alignSelf: "center" }}
                      onClick={() => {
                        // update the exact start time as startTime when user clicks the icon to book
                        setChosenTimes(prev => ({
                          ...prev,
                          startTime: DateTime.now().toFormat("HH:mm")
                        }));

                        if (availabilityTimeOnTheFlyData && availabilityTimeOnTheFlyFetched) {
                          setOpen(true);
                          setAvailabilityHours(
                            filterCurrentAvailabilityHours(availabilityTimeOnTheFlyData)
                          );
                        }
                      }}
                      size="large"
                    >
                      <AddLocation />
                    </IconButton>
                    <Typography variant={"caption"}>{t("book now")}</Typography>
                  </>
                )}
            </div>
          </>
        )}

        {(!locationGeoCodeByUidData || locationGeoCodeByUidData.length === 0) && (
          <Grid2
            container
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{ p: 1 }}
          >
            <CircularProgress />
            <Typography variant={"subtitle2"}>{t("_fetchingLocation")}</Typography>
          </Grid2>
        )}
      </Grid2>

      {/* mapping booked schedule card list */}
      {booking && (
        <CheckinBookingCard
          booking={booking}
          search={search}
          refetchCheckinInfo={refetchCheckinInfo}
        />
      )}

      <Dialog onClose={() => setOpen(false)} aria-labelledby="simple-dialog-title" open={open}>
        <Grid2
          container
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          sx={{ padding: 3 }}
        >
          <DialogTitle style={{ padding: "16px 0 0 0" }} id="simple-dialog-title">
            {t("Booking on the fly")}
          </DialogTitle>

          <Grid2 container justifyContent={"center"} alignItems={"center"} direction={"column"}>
            {availabilityHours && availabilityHours.timeframes.length > 0 && (
              <Grid2
                container
                justifyContent={"center"}
                alignItems={"center"}
                data-testid="multi-color-progress-bar-availability_times"
                height={60}
              >
                <Typography variant={"caption"}>{t("Availability Times")}</Typography>
                <MultiColorProgressBar
                  data={availabilityHours.timeframes.map(data => ({
                    locationName: data.startTime + " - " + data.endTime,
                    locationColor: data.available
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    relativeDuration: calcRelativeDurationForRemainingDay(
                      data.startTime,
                      data.endTime
                    ),
                    absoluteDuration: DateTime.fromISO(data.endTime)
                      .diff(DateTime.fromISO(data.startTime), "minutes")
                      .as("hours"),
                    locationInventoryId: 0,
                    isMobileWorking: false
                  }))}
                  percent
                />
              </Grid2>
            )}

            <TimePickers
              chosenTimes={chosenTimes}
              setChosenTimes={setChosenTimes}
              editable={{ start: true, end: true }}
            />
          </Grid2>

          <ChosenTimesWarnings chosenTimes={chosenTimes} />

          <Button
            data-testid={"confirm-bookAndcheckin-on-the-fly"}
            onClick={handleConfirmBookAndCheckinOntheFly}
            disabled={
              checkEndBeforeStart(
                DateTime.now().toFormat("yyyy-MM-dd"),
                chosenTimes.startTime,
                DateTime.now().toFormat("yyyy-MM-dd"),
                chosenTimes.endTime
              ) ||
              !position.fetchingDone ||
              !position.insideFence ||
              checkStartInPast(DateTime.now().toFormat("yyyy-MM-dd"), chosenTimes.startTime) ||
              loading
            }
            style={{ marginTop: 10 }}
            color={"primary"}
          >
            {t("book now")}
          </Button>
        </Grid2>
      </Dialog>
    </>
  );
};
